// Der Übersetzungsmechanismus des Editors — absichtlich klein.
//
// Deutsch ist die Quelle, Englisch die Übersetzung (`de.ts`, `en.ts`). Was
// dieses Modul leistet, ist dreierlei:
//
//   * Jeder sichtbare String des Editors steht an *einer* Stelle, erreichbar
//     über einen Schlüssel — neue Strings kommen nur über `t()` in die
//     Oberfläche (docs/EDITOR.md, „Sprache").
//   * Ein falscher Schlüssel ist ein tsc-Fehler, kein leerer Knopf: die
//     Schlüsseltypen werden aus dem Objekt in `de.ts` abgeleitet.
//   * Ein *fehlender* englischer Schlüssel ist ebenfalls ein tsc-Fehler:
//     `en.ts` ist als `typeof de` deklariert. Es gibt deshalb keinen stillen
//     Rückfall auf Deutsch — eine halb übersetzte Oberfläche kommt gar nicht
//     erst durch den Build.
//
// Welche Sprache gilt, entscheidet der Server, nicht das Bündel: das Layout
// rendert `<meta name="app-locale">` aus `I18n.locale`
// (ApplicationController#page_locale). Ein eigener Umschalter im Editor wäre
// eine zweite Wahrheit neben dem Umschalter im Footer.
//
// Platzhalter stehen als `{name}` im Text und werden aus `params` gefüllt.
// Für Mengen gibt es `tn(key, count)`: der Schlüssel zeigt auf ein Paar
// `{ one, other }`, und `{count}` ist der Platzhalter für die Zahl.

import { de } from './de'
import { en } from './en'

type Plural = { readonly one: string; readonly other: string }

// Alle Punkt-Pfade, die auf einen String enden …
type LeafPaths<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : T[K] extends Plural
      ? never
      : LeafPaths<T[K], `${Prefix}${K}.`>
}[keyof T & string]

// … und alle, die auf ein Paar `{ one, other }` enden.
type PluralPaths<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? never
    : T[K] extends Plural
      ? `${Prefix}${K}`
      : PluralPaths<T[K], `${Prefix}${K}.`>
}[keyof T & string]

export type TranslationKey = LeafPaths<typeof de>
export type PluralKey = PluralPaths<typeof de>

export type TranslationParams = Record<string, string | number>

/**
 * Das Wörterbuch dieser Seite.
 *
 * Einmal beim Laden des Moduls gelesen: die Locale steht im Markup, das der
 * Server ausgeliefert hat, und ändert sich ohne Neuladen nicht. Ohne Dokument
 * (vitest im Node-Projekt) und ohne Tag bleibt es bei Deutsch — dem Default
 * der App.
 */
function pageDictionary(): typeof de {
  if (typeof document === 'undefined') return de
  const locale = document.querySelector('meta[name="app-locale"]')?.getAttribute('content')
  return locale === 'en' ? en : de
}

const dictionary = pageDictionary()

// Ein Schlüssel wird pro Render vieler Komponenten nachgeschlagen; der Weg
// durch das Objekt ist billig, aber nicht kostenlos, und das Ergebnis ändert
// sich nie.
const cache = new Map<string, unknown>()

function lookup(key: string): unknown {
  const cached = cache.get(key)
  if (cached !== undefined) return cached

  let node: unknown = dictionary
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined
    node = (node as Record<string, unknown>)[part]
  }
  cache.set(key, node)
  return node
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  )
}

export function t(key: TranslationKey, params?: TranslationParams): string {
  const value = lookup(key)
  // Der Typ schließt das aus; zur Laufzeit ist der Schlüssel selbst immer
  // noch aussagekräftiger als ein leerer String.
  if (typeof value !== 'string') return key
  return interpolate(value, params)
}

export function tn(key: PluralKey, count: number, params?: TranslationParams): string {
  const value = lookup(key) as Plural | undefined
  if (!value || typeof value !== 'object') return key
  return interpolate(count === 1 ? value.one : value.other, { count, ...params })
}
